import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CheckCircle2, ArrowLeft, Save, RotateCcw, History, BadgeCheck, Lock } from "lucide-react";

import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { ConfirmPopupModal } from "../../components/ui/dialogModal";
import ToastMsg from "../../components/common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import {
    APPRAISALS,
    APPRAISAL_HR_REVIEW,
    APPRAISAL_PUBLISH,
} from "../../constants/api";

import StatusPill from "../../components/appraisal/StatusPill";
import KPIGoalRow from "../../components/appraisal/KPIGoalRow";
import AuditTimeline from "../../components/appraisal/AuditTimeline";
import ReviewPanel from "../../components/appraisal/ReviewPanel";
import FeedbackSummary from "../../components/appraisal/FeedbackSummary";
import SendBackModal from "../../components/appraisal/SendBackModal";
import useAppraisalRole from "../../hooks/useAppraisalRole";

// Reproduces the score → label mapping the backend uses (utils.score_to_rating_label).
// Used only as a preview before the server confirms the final value on approve/publish.
const ratingLabel = (score) => {
    if (score === null || score === undefined) return "Not Rated";
    const s = Number(score);
    if (s >= 4.5) return "Outstanding";
    if (s >= 3.5) return "Exceeds Expectations";
    if (s >= 2.5) return "Meets Expectations";
    if (s >= 1.5) return "Needs Improvement";
    return "Poor";
};

const computePreviewScore = (goals) => {
    let totalWeight = 0;
    let weightedSum = 0;
    for (const g of goals) {
        const rating = g.hr_override_rating ?? g.rm_rating ?? g.self_rating;
        if (rating === null || rating === undefined) continue;
        const w = g.weightage || 0;
        totalWeight += w;
        weightedSum += Number(rating) * w;
    }
    if (totalWeight === 0) return null;
    return Number((weightedSum / totalWeight).toFixed(2));
};

const reviewableStatuses = new Set(["pending_hr_approval", "hr_approved"]);

const FinalApprovalDetail = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { fetch, update, create } = useApiMethods();
    const { isHR } = useAppraisalRole();

    const [appraisal, setAppraisal] = useState(null);
    const [goals, setGoals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [confirmApprove, setConfirmApprove] = useState(false);
    const [confirmPublish, setConfirmPublish] = useState(false);
    const [sendBackOpen, setSendBackOpen] = useState(false);
    const [showHistory, setShowHistory] = useState(true);
    const [historyKey, setHistoryKey] = useState(0);

    const loadAppraisal = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(APPRAISALS, { id });
            setAppraisal(res?.data || null);
            setGoals(res?.data?.goals || []);
        } catch (_) {
            setAppraisal(null);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => { loadAppraisal(); }, [loadAppraisal]);

    if (loading) {
        return (
            <ContainerCard scrollable>
                <div className="text-center py-12 text-muted-foreground">Loading…</div>
            </ContainerCard>
        );
    }

    if (!appraisal) {
        return (
            <ContainerCard scrollable>
                <ModuleTitleCard Icon={CheckCircle2} title="Appraisal Not Found" />
                <div className="px-3">
                    <Button variant="outline" size="sm" onClick={() => navigate("/appraisal/final-approval")}>
                        <ArrowLeft className="w-4 h-4 mr-1" /> Back
                    </Button>
                </div>
            </ContainerCard>
        );
    }

    const canReview = isHR && reviewableStatuses.has(appraisal.status) && !appraisal.is_locked;
    const canPublish = isHR && appraisal.status === "hr_approved" && !appraisal.is_locked;
    // Prefer the server-confirmed final_score once it's set; otherwise show a live preview.
    const previewScore = appraisal.final_score ?? computePreviewScore(goals);

    const handleGoalChange = (updated) => {
        setGoals((prev) => prev.map((g) => (g.id === updated.id ? updated : g)));
    };

    const buildPayload = () => ({
        id,
        goals: goals.map((g) => ({
            id: g.id,
            hr_override_rating: g.hr_override_rating,
            hr_override_comment: g.hr_override_comment,
        })),
    });

    const saveDraft = async () => {
        setSaving(true);
        try {
            const res = await update(APPRAISALS, buildPayload());
            if (res?.status === "success") {
                ToastMsg("success", "HR notes saved");
                if (res.data) {
                    setAppraisal(res.data);
                    setGoals(res.data.goals || []);
                }
            } else {
                ToastMsg("error", res?.message || "Failed to save");
            }
        } catch (_) {
            ToastMsg("error", "Failed to save");
        } finally {
            setSaving(false);
        }
    };

    const approve = async () => {
        try {
            const res = await create(APPRAISAL_HR_REVIEW, {
                action: "approve",
                comment: "HR approved. Awaiting publishing.",
                ...buildPayload(),
            });
            if (res?.status === "success") {
                ToastMsg("success", "HR approved");
                if (res.data) {
                    setAppraisal(res.data);
                    setGoals(res.data.goals || []);
                }
                setHistoryKey((k) => k + 1);
            } else {
                ToastMsg("error", res?.message || "Failed to approve");
            }
        } catch (_) {
            ToastMsg("error", "Failed to approve");
        }
        setConfirmApprove(false);
    };

    const publish = async () => {
        try {
            const res = await create(APPRAISAL_PUBLISH, { id });
            if (res?.status === "success") {
                ToastMsg("success", "Appraisal published");
                if (res.data) {
                    setAppraisal(res.data);
                    setGoals(res.data.goals || []);
                }
                setHistoryKey((k) => k + 1);
            } else {
                ToastMsg("error", res?.message || "Failed to publish");
            }
        } catch (_) {
            ToastMsg("error", "Failed to publish");
        }
        setConfirmPublish(false);
    };

    const sendBack = async (comment) => {
        try {
            const res = await create(APPRAISAL_HR_REVIEW, {
                action: "send_back",
                comment,
                ...buildPayload(),
            });
            if (res?.status === "success") {
                ToastMsg("success", "Sent back to Reporting Manager");
                if (res.data) {
                    setAppraisal(res.data);
                    setGoals(res.data.goals || []);
                }
                setHistoryKey((k) => k + 1);
            } else {
                ToastMsg("error", res?.message || "Failed to send back");
            }
        } catch (_) {
            ToastMsg("error", "Failed to send back");
        }
        setSendBackOpen(false);
    };

    return (
        <ContainerCard scrollable>
            <ModuleTitleCard
                Icon={CheckCircle2}
                title={`Final Approval — ${appraisal.employee?.name || ""}`}
                subtitle={`${appraisal.cycle_name || ""} · RM: ${appraisal.reporting_manager?.name || "—"}`}
            />

            <div className="px-3 pb-6 space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <Button variant="ghost" size="sm" onClick={() => navigate("/appraisal/final-approval")} className="self-start">
                        <ArrowLeft className="w-4 h-4 mr-1" /> Back
                    </Button>
                    <div className="flex flex-wrap items-center gap-2">
                        <StatusPill value={appraisal.status} />
                        {appraisal.is_locked && (
                            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                                <Lock className="w-3 h-3" /> Locked
                            </span>
                        )}
                        <Button variant="outline" size="sm" onClick={() => setShowHistory((v) => !v)}>
                            <History className="w-4 h-4 mr-1" /> History
                        </Button>
                    </div>
                </div>

                {/* Final score summary */}
                <div className="rounded-lg border border-emerald-200 dark:border-emerald-900/40 bg-emerald-50/60 dark:bg-emerald-900/15 p-4 sm:p-5">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div>
                            <div className="flex items-center gap-2 text-xs text-emerald-700 dark:text-emerald-300">
                                <BadgeCheck className="w-4 h-4" />
                                <span className="font-medium">Final Score (weighted)</span>
                            </div>
                            <div className="flex items-baseline gap-2 mt-1">
                                <span className="text-3xl font-semibold text-emerald-900 dark:text-emerald-200">
                                    {previewScore ?? "—"}
                                </span>
                                <span className="text-sm text-emerald-700 dark:text-emerald-300">/ 5</span>
                            </div>
                            <p className="text-sm font-medium text-emerald-900 dark:text-emerald-200 mt-0.5">
                                {appraisal.final_rating || ratingLabel(previewScore)}
                            </p>
                        </div>
                        <div className="text-xs text-emerald-700 dark:text-emerald-300 max-w-md">
                            Computed using HR overrides where set, otherwise the Reporting Manager's rating, otherwise the employee's self-rating — weighted by each KPI's percentage. The server confirms the value on approve.
                        </div>
                    </div>
                </div>

                {/* 360 is something you act on, so it does not sit behind
                    the History button -- that is for the audit trail. Hidden
                    entirely on a cycle that does not run 360. */}
                {appraisal.cycle_feedback_enabled && (
                    <>
                        <ReviewPanel appraisalId={appraisal.id} mode="view" />
                        <FeedbackSummary appraisalId={appraisal.id} />
                    </>
                )}

                {showHistory && (
                    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-5">
                        <h3 className="text-base font-semibold mb-3">Activity History</h3>
                        <AuditTimeline appraisalId={appraisal.id} refreshKey={historyKey} />
                    </div>
                )}

                <div className="space-y-3">
                    {goals.map((g) => (
                        <KPIGoalRow
                            key={g.id}
                            goal={g}
                            role="hr"
                            readOnly={!canReview}
                            onChange={handleGoalChange}
                        />
                    ))}
                </div>

                {(canReview || canPublish) && (
                    <div className="flex flex-col sm:flex-row sm:justify-end gap-2 sticky bottom-0 bg-gradient-to-t from-white dark:from-gray-900 from-50% to-transparent pt-3 -mx-3 px-3 pb-1">
                        {canReview && (
                            <>
                                <Button variant="outline" onClick={saveDraft} disabled={saving}>
                                    <Save className="w-4 h-4 mr-1" /> {saving ? "Saving…" : "Save Draft"}
                                </Button>
                                <Button
                                    variant="outline"
                                    onClick={() => setSendBackOpen(true)}
                                    className="border-orange-300 text-orange-600 hover:bg-orange-50 dark:border-orange-700 dark:text-orange-400 dark:hover:bg-orange-900/20"
                                >
                                    <RotateCcw className="w-4 h-4 mr-1" /> Send Back to RM
                                </Button>
                                <Button
                                    variant="brand"
                                    onClick={() => setConfirmApprove(true)}
                                >
                                    <CheckCircle2 className="w-4 h-4 mr-1" /> HR Approve
                                </Button>
                            </>
                        )}
                        {canPublish && (
                            <Button
                                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                onClick={() => setConfirmPublish(true)}
                            >
                                <BadgeCheck className="w-4 h-4 mr-1" /> Publish
                            </Button>
                        )}
                    </div>
                )}
            </div>

            <ConfirmPopupModal
                open={confirmApprove}
                onClose={() => setConfirmApprove(false)}
                onConfirm={approve}
                title="HR Approve"
                description="Mark this appraisal as HR-approved? After approval you can publish it to make it visible to the employee."
                type="info"
                confirmLabel="Approve"
            />

            <ConfirmPopupModal
                open={confirmPublish}
                onClose={() => setConfirmPublish(false)}
                onConfirm={publish}
                title="Publish Appraisal"
                description="Once published, this appraisal becomes read-only and the final ratings are visible to the employee. This action cannot be undone."
                type="success"
                confirmLabel="Publish"
            />

            <SendBackModal
                open={sendBackOpen}
                onClose={() => setSendBackOpen(false)}
                onConfirm={sendBack}
                title="Send Back to Reporting Manager"
                actor="HR"
            />
        </ContainerCard>
    );
};

export default FinalApprovalDetail;
